import { IonContent, IonHeader, IonPage, useIonToast } from '@ionic/react';
import {
  AuthCredential,
  PhoneAuthCredential,
  updateEmail,
  updatePhoneNumber,
  updateProfile,
} from 'firebase/auth';
import React from 'react';
import styled from 'styled-components';
import { Profile, UnsavedProfile } from '../components/account/ProfileForm';
import AlertBanner from '../components/common/AlertBanner';
import Toolbar from '../components/common/Toolbar';
import { withAuthRequired } from '../services/buildfire/auth';
import { useFirebase } from '../services/firebase';
import EmailNotVerifiedWarning from '../components/user/EmailNotVerifiedWarning';
import { setUserMetadata } from '../services/firebase/user';
import { getDatabase } from 'firebase/database';
import Email from '../components/account/profile/Email';
import Name from '../components/account/profile/Name';
import PhoneNumber from '../components/account/profile/PhoneNumber';
import { warning } from 'ionicons/icons';
import ProfilePicture from '../components/account/profile/ProfilePicture';
import Notifications from '../components/account/profile/Notifications';
import { usePreferences } from '@trycourier/react-hooks';
import { useCourier } from '@trycourier/react-provider';

interface AccountProps {}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 80px 0 42px 0;
  padding: 0 16px;
`;

const ContentContainer = styled.div`
  max-width: 100%;
  width: 400px;
  margin: 48px 0;
`;

const Account: React.FC<AccountProps> = (props) => {
  const { currentUser, app, triggerUpdate, isAdmin, userMetadata } =
    useFirebase();
  const preferences = usePreferences();
  const courier = useCourier();

  React.useEffect(() => {
    if (!courier.userId) {
      return;
    }

    console.log('Fetching preferences for user', courier.userId);
    preferences.fetchPreferencePage();
    preferences.fetchRecipientPreferences();
  }, [courier.userId]);

  const [profile, setProfile] = React.useState<Profile | UnsavedProfile>({
    email: currentUser?.email || '',
    photoFile: null,
    phoneNumber: currentUser?.phoneNumber || '',
    displayName: currentUser?.displayName || '',
    // Pathologist only
    signature: userMetadata?.signature || '',
  });
  const [present] = useIonToast();

  const onSaveEmail = async (email: string, credential: AuthCredential) => {
    if (!currentUser) {
      console.error('Error: user not logged in');
      return false;
    }

    try {
      await updateEmail(currentUser, email);
      await setUserMetadata(getDatabase(app), currentUser, {
        email,
      });
      setProfile({ ...profile, email });
      present('Successfully updated email', 4000);
      return true;
    } catch (e) {
      console.error(e);
      present({
        message: (e as any).message || 'Error saving email, please try again',
        duration: 6000,
        color: 'danger',
        icon: warning,
      });
      return false;
    }
  };

  const onSaveDisplayName = async (displayName: string) => {
    if (!currentUser) {
      console.error('Error: user not logged in');
      return false;
    }

    try {
      await updateProfile(currentUser, { displayName });
      setProfile({ ...profile, displayName });
      present('Successfully updated display name', 4000);
      return true;
    } catch (e) {
      console.error(e);
      present({
        message: (e as any).message || 'Error saving profile, please try again',
        duration: 6000,
        color: 'danger',
        icon: warning,
      });
      return false;
    }
  };

  const onSavePhoneNumber = async (
    phoneNumber: string,
    authCredential: PhoneAuthCredential
  ) => {
    try {
      await updatePhoneNumber(currentUser!, authCredential);

      const db = getDatabase(app);
      await setUserMetadata(db, currentUser!, {
        phoneNumber: phoneNumber,
      });
      setProfile({ ...profile, phoneNumber });
      present('Successfully updated phone number', 4000);
      return true;
    } catch (e) {
      console.error(e);
      present({
        message: (e as any).message || 'Error updating phone number',
        duration: 6000,
        color: 'danger',
        icon: warning,
      });
      return false;
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <Toolbar showBackButton title="Account" />
        <AlertBanner />
      </IonHeader>
      <IonContent>
        <Container>
          {currentUser && !currentUser.emailVerified && (
            <EmailNotVerifiedWarning user={currentUser} />
          )}
          <ContentContainer>
            <ProfilePicture photoURL={currentUser?.photoURL || ''} />
            <Email
              currentUser={currentUser!}
              profile={profile}
              onSave={onSaveEmail}
            />
            <Name
              isAdmin={isAdmin}
              profile={profile}
              onSave={onSaveDisplayName}
            />
            <PhoneNumber profile={profile} onSave={onSavePhoneNumber} />
            <Notifications
              isLoading={preferences.isLoading || false}
              recipientPreferences={
                preferences.preferencePage?.sections
                  ? preferences.recipientPreferences || []
                  : []
              }
            />
          </ContentContainer>
        </Container>
      </IonContent>
    </IonPage>
  );
};

export default withAuthRequired(Account);
